import apiClient from './api';

// Types
export interface TextChatRequest {
  session_id?: string;
  course_id: string;
  module_id?: string;
  scene_id?: string;
  scene_number?: number;
  message: string;
  language?: string;
  auto_detect_language?: boolean;
  include_audio_response?: boolean;
  translate_to?: string[];
}

export interface AudioChatRequest {
  audio_file: File;
  session_id?: string;
  course_id: string;
  module_id?: string;
  scene_id?: string;
  scene_number?: number;
  language?: string;  // Single language for STT, LLM, and TTS
  return_audio?: boolean;
}

export interface ChatResponse {
  session_id: string;
  response_text: string;
  response_language: string;
  translations?: Record<string, string>;
  audio_url?: string;
  audio_duration?: number;
  context_info?: {
    course_title: string;
    module_title?: string;
    module_number?: number;
    scene_title?: string;
    scene_number?: number;
    scene_type?: 'video' | 'audio';
    current_position?: string;
  };
  suggestions?: string[];
  references?: Array<{
    type: 'module' | 'scene' | 'resource';
    id: string;
    title: string;
    relevance_score: number;
  }>;
  related_content?: Array<{
    scene_id: string;
    scene_number: number;
    scene_title: string;
    relevance: string;
  }>;
  timestamp?: string;
  detected_language?: string;
  response_time_ms?: number;
  // Additional fields for audio response
  user_transcript?: string; // Transcribed user audio
  input_text?: string; // Original input text
  transcribed_text?: string; // Another possible field for transcription
  data?: ChatResponse; // In case response is wrapped
}

export interface ConversationMessage {
  message_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  content_language: string;
  timestamp: string;
  scene_context?: {
    scene_id: string;
    scene_number: number;
    scene_title: string;
  };
  audio_url?: string;
}

export interface ConversationHistory {
  session_id: string;
  user_id: string;
  messages: ConversationMessage[];
  learning_context?: {
    course_id: string;
    course_title: string;
    module_title: string;
    scene_title: string;
  };
  session_metadata?: {
    started_at: string;
    last_activity_at: string;
    total_messages: number;
    languages_used: string[];
  };
}

export interface UpdateContextRequest {
  session_id: string;
  course_id: string;
  module_id?: string;
  scene_id?: string;
  scene_number?: number;
  force_reload?: boolean;
}

export interface SetLanguageRequest {
  session_id: string;
  preferred_language: string;
  auto_detect?: boolean;
}

export interface SupportedLanguage {
  code: string;
  name: string;
  native_name: string;
  supports_audio: boolean;
  supports_translation: boolean;
}

class LearningAssistantService {
  private baseUrl = '/api/learning-assistant';

  /**
   * Send a text message to the learning assistant
   */
  async sendTextMessage(request: TextChatRequest): Promise<ChatResponse> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/chat/text`, request);
      
      // Check if response is successful
      if (response.data.success === true && response.data.data) {
        return response.data.data; // Return the actual data object
      } else if (response.data.success !== false && !response.data.error) {
        return response.data; // Return as is if not wrapped
      }
      
      throw new Error(response.data.description || response.data.error?.error_description || 'Failed to send message');
    } catch (error: any) {
      console.error('Error sending text message:', error);
      throw error;
    }
  }

  /**
   * Send an audio message to the learning assistant
   */
  async sendAudioMessage(request: AudioChatRequest): Promise<ChatResponse> {
    try {
      
      const formData = new FormData();
      formData.append('audio_file', request.audio_file, request.audio_file.name);
      
      // Add other fields
      if (request.session_id) formData.append('session_id', request.session_id);
      formData.append('course_id', request.course_id);
      if (request.module_id) formData.append('module_id', request.module_id);
      if (request.scene_id) formData.append('scene_id', request.scene_id);
      if (request.scene_number) formData.append('scene_number', request.scene_number.toString());
      if (request.language) formData.append('language', request.language);
      // Send return_audio as string 'true' or 'false' for proper parsing
      if (request.return_audio !== undefined) {
        formData.append('return_audio', String(request.return_audio));
      }

      
      // Don't set Content-Type header - let axios set it with proper boundary
      const response = await apiClient.post(`${this.baseUrl}/chat/audio`, formData);
      
      // Check if response is successful
      if (response.data.success === true && response.data.data) {
        return response.data.data; // Return the actual data object
      } else if (response.data.success !== false && !response.data.error) {
        return response.data; // Return as is if not wrapped
      }
      
      throw new Error(response.data.description || response.data.error?.error_description || 'Failed to send audio message');
    } catch (error: any) {
      console.error('Error sending audio message:', error);
      throw error;
    }
  }

  /**
   * Get conversation history for a session
   */
  async getConversationHistory(sessionId: string): Promise<ConversationHistory> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/chat/history/${sessionId}`);
      
      if (response.data.success !== false) {
        return response.data;
      }
      
      throw new Error(response.data.description || 'Failed to get conversation history');
    } catch (error: any) {
      console.error('Error getting conversation history:', error);
      throw error;
    }
  }

  /**
   * Update the learning context for a session
   */
  async updateContext(request: UpdateContextRequest): Promise<{ message: string; session_id: string }> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/chat/context`, request);
      
      if (response.data.success !== false) {
        return response.data;
      }
      
      throw new Error(response.data.description || 'Failed to update context');
    } catch (error: any) {
      console.error('Error updating context:', error);
      throw error;
    }
  }

  /**
   * End a conversation session
   */
  async endSession(sessionId: string): Promise<{ message: string; session_id: string }> {
    try {
      const response = await apiClient.delete(`${this.baseUrl}/chat/session/${sessionId}`);
      
      if (response.data.success !== false) {
        return response.data;
      }
      
      throw new Error(response.data.description || 'Failed to end session');
    } catch (error: any) {
      console.error('Error ending session:', error);
      throw error;
    }
  }

  /**
   * Set language preference for a session
   */
  async setLanguagePreference(request: SetLanguageRequest & { course_id?: string }): Promise<{ message: string; session_id: string; language: string }> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/chat/language`, request);
      
      if (response.data.success !== false) {
        return response.data;
      }
      
      throw new Error(response.data.description || 'Failed to set language preference');
    } catch (error: any) {
      console.error('Error setting language preference:', error);
      throw error;
    }
  }

  /**
   * Get list of supported languages
   */
  async getSupportedLanguages(): Promise<{ languages: SupportedLanguage[] }> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/chat/languages`);
      
      if (response.data.success !== false) {
        return response.data;
      }
      
      throw new Error(response.data.description || 'Failed to get supported languages');
    } catch (error: any) {
      console.error('Error getting supported languages:', error);
      throw error;
    }
  }

  /**
   * Generate a unique session ID
   */
  generateSessionId(): string {
    return `las_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Store session ID in local storage
   */
  storeSessionId(courseId: string, sessionId: string): void {
    const key = `learning_assistant_session_${courseId}`;
    localStorage.setItem(key, sessionId);
  }

  /**
   * Retrieve session ID from local storage
   */
  getStoredSessionId(courseId: string): string | null {
    const key = `learning_assistant_session_${courseId}`;
    return localStorage.getItem(key);
  }

  /**
   * Clear session ID from local storage
   */
  clearStoredSessionId(courseId: string): void {
    const key = `learning_assistant_session_${courseId}`;
    localStorage.removeItem(key);
  }

  /**
   * Store language preference
   */
  storeLanguagePreference(language: string): void {
    localStorage.setItem('learning_assistant_language', language);
  }

  /**
   * Get stored language preference
   */
  getStoredLanguagePreference(): string | null {
    return localStorage.getItem('learning_assistant_language');
  }
}

export const learningAssistantService = new LearningAssistantService();
export default learningAssistantService;